// @owners { team: patient-fulfillment }
import { SPACING } from '@alto/design-library-tokens';
import {
  ActionSheetToast,
  Body,
  type DateData,
  DatePickerActionSheet,
  LgPadding,
  ListButton,
  toDateData,
} from '@alto/design-system';
import { DateRestrictionReasonMap } from '@alto/scriptdash/alto/scheduling/types/v1/date_restriction_reason';
import { format, parse } from 'date-fns';
import React, { useCallback, useState } from 'react';
import { CONTACT_US_ORIGIN } from '~shared/constants';
import { getInvalidDateKey } from '~shared/features/cart/hooks/useCartDateInfoKey';
import { useCartNextAvailableDate } from '~shared/features/cart/hooks/useCartNextAvailableDate';
import { dateInfoKeysForToast, getDateValidationErrors } from '~shared/features/checkout/hooks/useCheckoutDateErrors';
import { getOrderDate } from '~shared/features/checkout/selectors/getOrder';
import { getDisabledDates } from '~shared/features/next-available-date/helpers/getDisabledDates';
import { useQueryShipmentFeesAvailable } from '~shared/queries';
import { useSelectorShared } from '~shared/store';
import { ContactUs } from '../../../alto-assistant';
import { getNextAvailableDateTitle } from '../delivery-date-card';
import { getCalendarPickerMinDate, getInitialSelectedDate } from './utils';

type Props = {
  readonly handleSelectCustomDate: (date: DateData) => void;
  readonly openGetItSoonerFlow: () => void;
};

export const SelectDateActionSheet = ({ handleSelectCustomDate, openGetItSoonerFlow }: Props) => {
  const [toast, setToast] = useState('');
  const orderDate = useSelectorShared(getOrderDate);
  const {
    earliestAvailableDate,
    lastAvailableDate,
    lastAvailableDateReason,
    excludedDates = [],
  } = useCartNextAvailableDate();
  const { feeToDateHash, dateToFeeHash } = useQueryShipmentFeesAvailable();
  const formattedLastAvailableDate = lastAvailableDate
    ? format(parse(lastAvailableDate, 'yyyy-MM-dd', 0), 'EEE, MMM do')
    : null;
  const hasProgynyAuthEndDate = !!(
    lastAvailableDate &&
    lastAvailableDateReason === DateRestrictionReasonMap.PROGYNY_AUTHORIZATION_EXPIRATION &&
    formattedLastAvailableDate
  );
  const minDate = getCalendarPickerMinDate({
    feeToDateHash,
    earliestAvailableDate,
  });
  const initialSelectedDate = getInitialSelectedDate({
    minDate,
    orderDate,
    dateToFeeHash,
  });
  const nextAvailableDateTitle = getNextAvailableDateTitle(earliestAvailableDate);
  const disabledDates = getDisabledDates(excludedDates);

  const validateDate = useCallback(
    (date: DateData) => {
      const parsedDate = new Date(date.timestamp);
      const invalidDateKey = getInvalidDateKey({
        date: parsedDate,
        earliestAvailableDate,
        excludedDates,
        lastAvailableDate,
      });

      if (invalidDateKey && dateInfoKeysForToast.has(invalidDateKey)) {
        const errors = getDateValidationErrors({
          dateInfoKeyApplied: invalidDateKey,
          earliestAvailableDate,
          lastAvailableDate,
        });
        // there's currently no way to show a toast above a react-native modal
        // create a toast manually to show above the modal content for native and web
        setToast(errors[0].message);
      }
    },
    [earliestAvailableDate, excludedDates, lastAvailableDate],
  );

  return (
    <DatePickerActionSheet
      onClose={handleSelectCustomDate}
      disabledDates={disabledDates}
      modalTitle="Select delivery date"
      selectedDate={toDateData(initialSelectedDate)}
      minDate={minDate}
      maxDate={lastAvailableDate}
      onDayPress={validateDate}
      testID="select-delivery-date"
    >
      <LgPadding bottomPadding={SPACING.STATIC.XL}>
        <ListButton
          label={nextAvailableDateTitle}
          onPress={openGetItSoonerFlow}
          rightIcon="info"
        />
      </LgPadding>
      {hasProgynyAuthEndDate ? (
        <LgPadding>
          <Body>
            Your Progyny authorization is valid until {formattedLastAvailableDate}. If you need to schedule a delivery
            beyond your active coverage dates, please send us a message, or if urgent, please{' '}
            <ContactUs
              origin={CONTACT_US_ORIGIN.DatePickerActionSheet}
              type="link"
              method="phone"
            />
            .
          </Body>
        </LgPadding>
      ) : null}
      {toast ? (
        <ActionSheetToast
          variant="error"
          dismissible
          onHide={() => {
            setToast('');
          }}
        >
          {toast}
        </ActionSheetToast>
      ) : null}
    </DatePickerActionSheet>
  );
};
